import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../lib/AuthContext';
import './Dashboard.css';
import ArticlePreview from '../components/ArticlePreview';
import ArticleEditor, { imgsMissingAlt } from '../components/ArticleEditor';
import GenrePicker from '../components/GenrePicker';
import SettingsPanel from '../components/SettingsPanel';
import { uploadImage } from '../lib/uploadImage';
import orangeLogo from '../assets/orangelog.png';

const T = {
  accent:  '#dd3333',
  header:  '#111111',
  border:  '#e8e8e8',
  white:   '#fff',
};

const STATUS_COLORS = { draft: '#dc3545', pending: '#e67e22', approved: '#28a745', rejected: '#dc3545' };
const STATUS_LABELS = { draft: 'DRAFT', pending: 'Pending', approved: 'Approved', rejected: 'Rejected' };

function EditorDashboard() {
  const navigate = useNavigate();
  const { companyName, username } = useParams();

  const { accessToken: token, clearSession } = useAuth();
  const userType    = localStorage.getItem('userType');
  const storedUser  = localStorage.getItem('username');
  const displayName = localStorage.getItem('displayName') || storedUser;
  const storedCo    = localStorage.getItem('companyName');

  useEffect(() => {
    if (!token || userType !== 'editor') { navigate('/login'); return; }
    if (
      decodeURIComponent(username) !== storedUser ||
      decodeURIComponent(companyName) !== storedCo
    ) {
      navigate('/login');
    }
  }, []);

  const authHeader = { Authorization: `Bearer ${token}` };

  const [queue, setQueue]         = useState([]);
  const [reviewed, setReviewed]   = useState([]);
  const [myArticles, setMyArticles] = useState([]);
  const [authors, setAuthors]     = useState([]);
  const [loading, setLoading]     = useState(true);
  const [tab, setTab]             = useState('queue'); // 'queue' | 'reviewed' | 'my-articles'
  const [rejectTarget, setRejectTarget] = useState(null);
  const [rejectNote, setRejectNote]     = useState('');
  const [actionMsg, setActionMsg]       = useState('');
  const [previewArticle, setPreviewArticle] = useState(null);
  const [editTarget, setEditTarget]     = useState(null);
  const [editForm, setEditForm]         = useState({ title: '', content: '', editNote: '' });
  const [editSaving, setEditSaving]     = useState(false);

  // Create article form state
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [createForm, setCreateForm] = useState({
    title: '', writer_name: '', content: '', audience: 'local',
    locations: '', token_cost: 0, headline_image: null, external_url: '',
  });
  const [createMode, setCreateMode] = useState('article'); // 'article' | 'external'
  const [createGenreTree, setCreateGenreTree] = useState({ main: '', sub: '' });
  const [createGenrePickerKey, setCreateGenrePickerKey] = useState(0);
  const [createError, setCreateError] = useState('');
  const [createSaving, setCreateSaving] = useState(false);
  const [headlineUploading, setHeadlineUploading] = useState(false);

  // Edit own article
  const [editOwnTarget, setEditOwnTarget] = useState(null);
  const [editOwnForm, setEditOwnForm] = useState({ title: '', content: '', editNote: '' });
  const [editOwnSaving, setEditOwnSaving] = useState(false);

  const fetchAll = async () => {
    try {
      setLoading(true);
      const [qRes, rRes, meRes, myRes] = await Promise.all([
        axios.get('/api/editors/me/queue',    { headers: authHeader }),
        axios.get('/api/editors/me/reviewed', { headers: authHeader }),
        axios.get('/api/editors/me',          { headers: authHeader }),
        axios.get('/api/editors/me/articles', { headers: authHeader }),
      ]);
      setQueue(qRes.data);
      setReviewed(rRes.data);
      setAuthors(meRes.data.authors || []);
      setMyArticles(myRes.data);
    } catch (err) {
      // silent — auth guard handles redirect
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchAll(); }, []);

  const handleApprove = async (articleId, title) => {
    setActionMsg('');
    try {
      await axios.patch(`/api/editors/me/articles/${articleId}/approve`, {}, { headers: authHeader });
      setActionMsg(`"${title}" approved and is now live.`);
      fetchAll();
    } catch (err) {
      setActionMsg(err.response?.data?.error || 'Failed to approve.');
    }
  };

  const handleRejectSubmit = async () => {
    if (!rejectTarget) return;
    setActionMsg('');
    try {
      await axios.patch(`/api/editors/me/articles/${rejectTarget._id}/reject`, { note: rejectNote }, { headers: authHeader });
      setActionMsg(`"${rejectTarget.title}" has been rejected.`);
      setRejectTarget(null);
      setRejectNote('');
      fetchAll();
    } catch (err) {
      setActionMsg(err.response?.data?.error || 'Failed to reject.');
    }
  };

  const openEditorEdit = (art) => {
    setEditTarget(art);
    setEditForm({ title: art.title, content: art.content || '', editNote: '' });
    setActionMsg('');
  };

  const handleEditorEditSave = async () => {
    if (!editTarget) return;
    const missing = imgsMissingAlt(editForm.content);
    if (missing > 0) {
      setActionMsg(`${missing} image${missing > 1 ? 's are' : ' is'} missing alt text. Add accessibility descriptions to all images before saving.`);
      return;
    }
    setEditSaving(true);
    setActionMsg('');
    try {
      await axios.patch(
        `/api/editors/me/articles/${editTarget._id}/edit`,
        { title: editForm.title, content: editForm.content, editNote: editForm.editNote },
        { headers: authHeader }
      );
      setActionMsg(`"${editForm.title}" saved.`);
      setEditTarget(null);
      fetchAll();
    } catch (err) {
      setActionMsg(err.response?.data?.error || 'Failed to save edit.');
    } finally {
      setEditSaving(false);
    }
  };

  const handleCreateSubmit = async (e, saveAsDraft = false) => {
    e.preventDefault();
    setCreateError('');
    if (!createGenreTree.main) { setCreateError('Please select a genre category.'); return; }
    if (!saveAsDraft) {
      const missing = imgsMissingAlt(createForm.content);
      if (missing > 0) {
        setCreateError(`${missing} image${missing > 1 ? 's are' : ' is'} missing alt text. Click each image in the editor and add an accessibility description before publishing.`);
        return;
      }
    }
    setCreateSaving(true);
    try {
      const articleData = {
        article_id: `${decodeURIComponent(companyName).replace(/\s+/g, '-').toLowerCase()}-ed-${Date.now()}`,
        writer_name: createForm.writer_name
          ? createForm.writer_name.split(',').map(n => n.trim()).filter(n => n)
          : [displayName],
        title:       createForm.title,
        content:     createForm.content,
        genre_tree:  createGenreTree,
        audience:    createForm.audience,
        locations:   createForm.locations.split(',').map(l => l.trim()).filter(l => l),
        token_cost:  Math.min(99, Math.max(0, parseFloat(createForm.token_cost) || 0)),
        headline_image: createForm.headline_image || null,
        ...(createMode === 'external' ? { external_url: createForm.external_url } : {}),
        pricingMode: 'manual',
        ...(saveAsDraft ? { status: 'draft' } : {}),
      };
      await axios.post('/api/articles', articleData, { headers: authHeader });
      setShowCreateForm(false);
      setCreateMode('article');
      setCreateForm({ title: '', writer_name: '', content: '', audience: 'local', locations: '', token_cost: 0, headline_image: null, external_url: '' });
      setCreateGenreTree({ main: '', sub: '' });
      setCreateGenrePickerKey(k => k + 1);
      fetchAll();
      if (tab !== 'my-articles') setTab('my-articles');
    } catch (err) {
      setCreateError(err.response?.data?.error || 'Failed to create article.');
    } finally {
      setCreateSaving(false);
    }
  };

  const handlePublishDraft = async (articleId) => {
    try {
      await axios.patch(`/api/editors/me/articles/${articleId}/publish`, {}, { headers: authHeader });
      fetchAll();
    } catch (err) {
      setActionMsg(err.response?.data?.error || 'Failed to publish draft.');
    }
  };

  const openEditOwn = (art) => {
    setEditOwnTarget(art);
    setEditOwnForm({ title: art.title, content: art.content || '', editNote: '' });
  };

  const handleEditOwnSave = async () => {
    if (!editOwnTarget) return;
    const missing = imgsMissingAlt(editOwnForm.content);
    if (missing > 0) {
      setActionMsg(`${missing} image${missing > 1 ? 's are' : ' is'} missing alt text. Add accessibility descriptions to all images before saving.`);
      return;
    }
    setEditOwnSaving(true);
    try {
      await axios.patch(
        `/api/editors/me/articles/${editOwnTarget._id}/edit-own`,
        { title: editOwnForm.title, content: editOwnForm.content, editNote: editOwnForm.editNote },
        { headers: authHeader }
      );
      setEditOwnTarget(null);
      fetchAll();
    } catch (err) {
      setActionMsg(err.response?.data?.error || 'Failed to save.');
    } finally {
      setEditOwnSaving(false);
    }
  };

  const handleHeadlineUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { setCreateError('Image must be under 5 MB.'); return; }
    setHeadlineUploading(true);
    try {
      const url = await uploadImage(file, token);
      setCreateForm(f => ({ ...f, headline_image: url }));
    } catch (err) {
      setCreateError('Image upload failed.');
    } finally {
      setHeadlineUploading(false);
    }
  };

  const handleLogout = () => {
    if (token) axios.post('/api/auth/logout', {}, { headers: { Authorization: `Bearer ${token}` } }).catch(() => {});
    clearSession();
    navigate('/');
  };

  const cardStyle = (status) => ({
    backgroundColor: status === 'draft' ? '#fff5f5' : '#fff',
    border: `1px solid ${status === 'draft' ? '#f5c6cb' : status === 'pending' ? '#f5e0c8' : status === 'approved' ? '#c8ecd4' : '#f5c8c8'}`,
    borderLeft: `4px solid ${STATUS_COLORS[status] || '#888'}`,
    borderRadius: '8px',
    padding: '1rem 1.25rem',
    marginBottom: '0.85rem',
  });

  const ArticleCard = ({ art, showActions }) => (
    <div style={cardStyle(art.status)}>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '1rem', flexWrap: 'wrap' }}>
        <div style={{ flex: 1 }}>
          {art.genre_tree?.main && (
            <div style={{ fontSize: '0.72rem', color: '#888', marginBottom: '0.2rem', letterSpacing: '0.03em' }}>
              {art.genre_tree.main}{art.genre_tree.sub ? ` / ${art.genre_tree.sub.split(' › ').join(' / ')}` : ''}
            </div>
          )}
          <div style={{ fontWeight: '700', fontSize: '0.95rem', color: '#1a1a1a', marginBottom: '0.3rem' }}>
            {art.title}
            {art.external_url && (
              <span style={{ display: 'inline-block', fontSize: '0.68rem', fontWeight: '700', background: '#e8f4fd', color: '#0066cc', border: '1px solid #b8d9f5', borderRadius: '4px', padding: '1px 6px', marginLeft: '8px', verticalAlign: 'middle' }}>🔗 External</span>
            )}
          </div>
          <div style={{ fontSize: '0.82rem', color: '#555', display: 'flex', flexWrap: 'wrap', gap: '1rem', marginBottom: '0.2rem' }}>
            {art.authorInfo
              ? <span>Written By: <strong>{art.authorInfo.display_name}</strong></span>
              : art.writer_name?.length > 0 && <span>Written By: <strong>{Array.isArray(art.writer_name) ? art.writer_name.join(', ') : art.writer_name}</strong></span>
            }
            <span>Published: {new Date(art.date_created).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</span>
          </div>
          <div style={{ fontSize: '0.8rem', color: '#888', display: 'flex', flexWrap: 'wrap', gap: '0.75rem' }}>
            <span>{art.token_cost} tokens</span>
            {art.kudos > 0 && <span style={{ color: '#7b2ff7' }}>👍 {art.kudos}</span>}
          </div>
          {art.editorNote && (
            <div style={{ marginTop: '0.5rem', fontSize: '0.8rem', color: '#dc3545', fontStyle: 'italic' }}>
              Note: {art.editorNote}
            </div>
          )}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexShrink: 0 }}>
          <span style={{ fontSize: '0.75rem', fontWeight: '700', color: STATUS_COLORS[art.status] || '#888', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
            {STATUS_LABELS[art.status] || art.status}
          </span>
          <button
            onClick={() => setPreviewArticle(art)}
            style={{ padding: '0.3rem 0.75rem', backgroundColor: '#f4f4f4', color: '#333', border: '1px solid #ddd', borderRadius: '5px', fontSize: '0.82rem', cursor: 'pointer', fontWeight: '600' }}
          >
            Preview
          </button>
          <button
            onClick={() => openEditorEdit(art)}
            style={{ padding: '0.3rem 0.75rem', backgroundColor: '#fff', color: '#333', border: '1px solid #aaa', borderRadius: '5px', fontSize: '0.82rem', cursor: 'pointer', fontWeight: '600' }}
          >
            ✏️ Edit
          </button>
          {showActions && (
            <>
              <button
                onClick={() => handleApprove(art._id, art.title)}
                style={{ padding: '0.3rem 0.75rem', backgroundColor: '#28a745', color: '#fff', border: 'none', borderRadius: '5px', fontSize: '0.82rem', cursor: 'pointer', fontWeight: '600' }}
              >
                Approve
              </button>
              <button
                onClick={() => { setRejectTarget(art); setRejectNote(''); setActionMsg(''); }}
                style={{ padding: '0.3rem 0.75rem', backgroundColor: '#fff', color: '#dc3545', border: '1px solid #dc3545', borderRadius: '5px', fontSize: '0.82rem', cursor: 'pointer', fontWeight: '600' }}
              >
                Reject
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );

  const MyArticleCard = ({ art }) => {
    const isDraft = art.status === 'draft';
    return (
      <div style={cardStyle(art.status)}>
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '1rem', flexWrap: 'wrap' }}>
          <div style={{ flex: 1 }}>
            {art.genre_tree?.main && (
              <div style={{ fontSize: '0.72rem', color: '#888', marginBottom: '0.2rem', letterSpacing: '0.03em' }}>
                {art.genre_tree.main}{art.genre_tree.sub ? ` / ${art.genre_tree.sub.split(' › ').join(' / ')}` : ''}
              </div>
            )}
            <div style={{ fontWeight: '700', fontSize: '0.95rem', color: '#1a1a1a', marginBottom: '0.3rem' }}>
              {art.title}
              {art.external_url && (
                <span style={{ display: 'inline-block', fontSize: '0.68rem', fontWeight: '700', background: '#e8f4fd', color: '#0066cc', border: '1px solid #b8d9f5', borderRadius: '4px', padding: '1px 6px', marginLeft: '8px', verticalAlign: 'middle' }}>🔗 External</span>
              )}
            </div>
            <div style={{ fontSize: '0.82rem', color: '#555', display: 'flex', flexWrap: 'wrap', gap: '1rem', marginBottom: '0.2rem' }}>
              {art.writer_name?.length > 0 && (
                <span>By: <strong>{Array.isArray(art.writer_name) ? art.writer_name.join(', ') : art.writer_name}</strong></span>
              )}
              <span>{new Date(art.date_created).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</span>
              <span>{art.token_cost} tokens</span>
              <span>🛒 {art.purchased || 0} purchases</span>
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexShrink: 0 }}>
            <span style={{ fontSize: '0.75rem', fontWeight: '700', color: STATUS_COLORS[art.status] || '#888', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
              {STATUS_LABELS[art.status] || art.status}
            </span>
            <button
              onClick={() => setPreviewArticle(art)}
              style={{ padding: '0.3rem 0.75rem', backgroundColor: '#f4f4f4', color: '#333', border: '1px solid #ddd', borderRadius: '5px', fontSize: '0.82rem', cursor: 'pointer', fontWeight: '600' }}
            >
              Preview
            </button>
            <button
              onClick={() => openEditOwn(art)}
              style={{ padding: '0.3rem 0.75rem', backgroundColor: '#fff', color: '#333', border: '1px solid #aaa', borderRadius: '5px', fontSize: '0.82rem', cursor: 'pointer', fontWeight: '600' }}
            >
              ✏️ Edit
            </button>
            {isDraft && (
              <button
                onClick={() => handlePublishDraft(art._id)}
                style={{ padding: '0.3rem 0.75rem', backgroundColor: '#28a745', color: '#fff', border: 'none', borderRadius: '5px', fontSize: '0.82rem', cursor: 'pointer', fontWeight: '600' }}
              >
                ✅ Publish
              </button>
            )}
          </div>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="dashboard-container">
        <nav style={{ background: T.header, display: 'grid', gridTemplateColumns: '220px 1fr 300px', gridTemplateRows: '110px 36px', position: 'sticky', top: 0, zIndex: 1000, boxShadow: '0 2px 12px rgba(0,0,0,0.6)' }}>
          <div style={{ gridColumn: 1, gridRow: '1 / 3', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#F41238' }}>
            <img src={orangeLogo} alt="PubSilo" style={{ height: '90%', maxWidth: '210px', objectFit: 'contain', cursor: 'pointer' }} onClick={() => navigate('/')} />
          </div>
          <div style={{ gridColumn: 2, gridRow: 1, background: '#13273F' }} />
          <div style={{ gridColumn: 3, gridRow: '1 / 3', background: '#13273F' }} />
          <div style={{ gridColumn: 2, gridRow: 2, background: '#13273F', borderBottom: '2px solid #000' }} />
        </nav>
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
          <p>Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="dashboard-container">
      <nav style={{ background: T.header, display: 'grid', gridTemplateColumns: '220px 1fr 300px', gridTemplateRows: '110px 36px', position: 'sticky', top: 0, zIndex: 1000, boxShadow: '0 2px 12px rgba(0,0,0,0.6)' }}>
        {/* Logo — spans both rows */}
        <div style={{ gridColumn: 1, gridRow: '1 / 3', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#F41238' }}>
          <img src={orangeLogo} alt="PubSilo" style={{ height: '90%', maxWidth: '210px', objectFit: 'contain', cursor: 'pointer' }} onClick={() => navigate('/')} />
        </div>

        {/* Center: dashboard title */}
        <div style={{ gridColumn: 2, gridRow: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#13273F' }}>
          <div style={{ textAlign: 'center' }}>
            <div style={{ color: '#aaa', fontSize: '13px', fontFamily: 'Arial, sans-serif', letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: '6px' }}>
              {decodeURIComponent(companyName)}
            </div>
            <div style={{ color: T.white, fontSize: '28px', fontWeight: '300', fontFamily: 'Arial, sans-serif', letterSpacing: '0.04em', lineHeight: 1 }}>
              Editor Dashboard
            </div>
          </div>
        </div>

        {/* Right: user info + logout — spans both rows */}
        <div style={{ gridColumn: 3, gridRow: '1 / 3', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '8px', background: '#13273F' }}>
          <div style={{ color: T.white, fontSize: '15px', fontFamily: 'Arial, sans-serif', fontWeight: '700' }}>{displayName}</div>
          <div style={{ color: '#aaa', fontSize: '11px', fontFamily: 'Arial, sans-serif', textTransform: 'uppercase', letterSpacing: '0.1em' }}>Editor</div>
          <button
            onClick={handleLogout}
            style={{ marginTop: '4px', padding: '6px 16px', background: 'transparent', border: '1px solid #3a3a3a', borderRadius: '5px', color: '#ccc', fontSize: '12px', fontFamily: 'Arial, sans-serif', cursor: 'pointer' }}
            onMouseEnter={e => { e.currentTarget.style.background = '#222'; e.currentTarget.style.color = '#fff'; }}
            onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = '#ccc'; }}
          >
            Log Out
          </button>
        </div>

        {/* Row 2: tab nav strip */}
        <div style={{ gridColumn: 2, gridRow: 2, display: 'flex', alignItems: 'center', background: '#13273F', borderBottom: '2px solid #000' }}>
          {[
            { id: 'queue',       label: `Review Queue${queue.length > 0 ? ` (${queue.length})` : ''}` },
            { id: 'reviewed',    label: `Reviewed (${reviewed.length})` },
            { id: 'my-articles', label: `My Articles (${myArticles.length})` },
          ].map(t => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              style={{
                padding: '0 20px', height: '100%', background: tab === t.id ? 'rgba(255,255,255,0.08)' : 'transparent',
                border: 'none', borderBottom: tab === t.id ? `2px solid ${T.accent}` : '2px solid transparent',
                color: tab === t.id ? T.white : '#aaa',
                fontSize: '11px', fontWeight: '800', textTransform: 'uppercase', letterSpacing: '0.08em',
                fontFamily: 'Arial, sans-serif', cursor: 'pointer', marginBottom: '-2px',
                transition: 'background 0.12s, color 0.12s',
              }}
              onMouseEnter={e => { if (tab !== t.id) e.currentTarget.style.color = '#fff'; }}
              onMouseLeave={e => { if (tab !== t.id) e.currentTarget.style.color = '#aaa'; }}
            >
              {t.label}
            </button>
          ))}
        </div>
      </nav>

      <div className="dashboard-wrapper">
        {/* Left Sidebar */}
        <div className="sidebar sidebar-left">
          <div className="sidebar-title">Editor Info</div>
          <div className="stats">
            <div className="stat-item">
              <div className="stat-value">{displayName}</div>
              <div className="stat-label">Editor</div>
            </div>
            <div className="stat-item">
              <div className="stat-value" style={{ fontSize: '0.95rem', color: '#666' }}>{decodeURIComponent(companyName)}</div>
              <div className="stat-label">Company</div>
            </div>
            <div className="stat-item">
              <div className="stat-value" style={{ color: queue.length > 0 ? '#e67e22' : '#1a1a1a' }}>{queue.length}</div>
              <div className="stat-label">Pending Review</div>
            </div>
            <div className="stat-item">
              <div className="stat-value">{reviewed.filter(a => a.status === 'approved').length}</div>
              <div className="stat-label">Approved</div>
            </div>
            <div className="stat-item">
              <div className="stat-value">{reviewed.filter(a => a.status === 'rejected').length}</div>
              <div className="stat-label">Rejected</div>
            </div>
            <div className="stat-item">
              <div className="stat-value">{myArticles.filter(a => a.status === 'approved').length}</div>
              <div className="stat-label">My Live Articles</div>
            </div>
            {myArticles.some(a => a.status === 'draft') && (
              <div className="stat-item">
                <div className="stat-value" style={{ color: '#dc3545' }}>{myArticles.filter(a => a.status === 'draft').length}</div>
                <div className="stat-label">My Drafts</div>
              </div>
            )}
          </div>

          {authors.length > 0 && (
            <>
              <div className="sidebar-title" style={{ marginTop: '1.5rem' }}>My Authors</div>
              {authors.map(a => (
                <div key={a._id} style={{ marginBottom: '0.5rem', fontSize: '0.82rem' }}>
                  <div style={{ fontWeight: '600', color: '#333' }}>{a.display_name}</div>
                  <div style={{ color: '#888', fontSize: '0.76rem' }}>@{a.username}</div>
                </div>
              ))}
            </>
          )}
          <SettingsPanel onLogout={handleLogout} />
        </div>

        {/* Main content */}
        <div className="main-content">
          {actionMsg && (
            <div style={{
              padding: '0.65rem 1rem', borderRadius: '7px', marginBottom: '1rem', fontSize: '0.875rem',
              backgroundColor: actionMsg.includes('live') || actionMsg.includes('approved') ? '#d4edda' : '#f8d7da',
              color: actionMsg.includes('live') || actionMsg.includes('approved') ? '#155724' : '#721c24',
              border: `1px solid ${actionMsg.includes('live') || actionMsg.includes('approved') ? '#c3e6cb' : '#f5c6cb'}`,
            }}>
              {actionMsg}
            </div>
          )}

          {tab === 'queue' && (
            queue.length === 0
              ? <div style={{ textAlign: 'center', color: '#aaa', padding: '3rem' }}>No articles awaiting review.</div>
              : queue.map(art => <ArticleCard key={art._id} art={art} showActions={true} />)
          )}

          {tab === 'reviewed' && (
            reviewed.length === 0
              ? <div style={{ textAlign: 'center', color: '#aaa', padding: '3rem' }}>No reviewed articles yet.</div>
              : reviewed.map(art => <ArticleCard key={art._id} art={art} showActions={false} />)
          )}

          {tab === 'my-articles' && (
            <>
              {showCreateForm ? (
                <div style={{ backgroundColor: '#fff', border: '1px solid #e0e0e0', borderRadius: '10px', padding: '1.5rem', marginBottom: '1.5rem' }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1.25rem' }}>
                    <h3 style={{ margin: 0, fontSize: '1.05rem' }}>✍️ Create New Article</h3>
                    <button onClick={() => { setShowCreateForm(false); setCreateError(''); setCreateMode('article'); }} style={{ background: 'none', border: 'none', fontSize: '1.3rem', cursor: 'pointer', color: '#aaa' }}>×</button>
                  </div>
                  <form onSubmit={handleCreateSubmit}>
                    {/* Mode toggle */}
                    <div style={{ display: 'flex', gap: '0.4rem', marginBottom: '1.25rem', padding: '0.25rem', background: '#f0f0f0', borderRadius: '7px', width: 'fit-content' }}>
                      {[{ id: 'article', label: '✍️ Full Article' }, { id: 'external', label: '🔗 External Link' }].map(m => (
                        <button key={m.id} type="button" onClick={() => setCreateMode(m.id)}
                          style={{ padding: '0.4rem 1rem', border: 'none', borderRadius: '5px', fontSize: '0.85rem', fontWeight: '600', cursor: 'pointer', background: createMode === m.id ? '#1a1a1a' : 'transparent', color: createMode === m.id ? '#fff' : '#666', transition: 'background 0.15s' }}
                        >{m.label}</button>
                      ))}
                    </div>
                    {createMode === 'external' && (
                      <div className="form-group">
                        <label>External URL <span style={{ color: '#dc3545' }}>*</span></label>
                        <input type="url" value={createForm.external_url} onChange={e => setCreateForm(f => ({ ...f, external_url: e.target.value }))} required={createMode === 'external'} placeholder="https://yoursite.com/article-slug" style={{ width: '100%' }} />
                        <div style={{ fontSize: '0.78rem', color: '#888', marginTop: '0.3rem' }}>Readers are sent to this URL after purchasing access. Publishes immediately (no review needed for editors).</div>
                      </div>
                    )}
                    <div className="form-group">
                      <label>Title</label>
                      <input type="text" value={createForm.title} onChange={e => setCreateForm(f => ({ ...f, title: e.target.value }))} required placeholder="Article headline" style={{ width: '100%' }} />
                    </div>
                    <div className="form-group">
                      <label>Byline / Writer Name</label>
                      <input type="text" value={createForm.writer_name} onChange={e => setCreateForm(f => ({ ...f, writer_name: e.target.value }))} placeholder={`Defaults to ${displayName}`} style={{ width: '100%' }} />
                    </div>
                    <div className="form-group">
                      <label>Genre</label>
                      <GenrePicker
                        key={createGenrePickerKey}
                        initialValue={createGenreTree}
                        onChange={setCreateGenreTree}
                      />
                    </div>
                    <div className="form-group">
                      <label>Audience</label>
                      <select value={createForm.audience} onChange={e => setCreateForm(f => ({ ...f, audience: e.target.value }))} style={{ width: '100%' }}>
                        {['world','national','regional','state','local'].map(a => (
                          <option key={a} value={a}>{a.charAt(0).toUpperCase() + a.slice(1)}</option>
                        ))}
                      </select>
                    </div>
                    <div className="form-group">
                      <label>Locations <span style={{ fontWeight: 400, color: '#888', fontSize: '0.82rem' }}>(comma-separated)</span></label>
                      <input type="text" value={createForm.locations} onChange={e => setCreateForm(f => ({ ...f, locations: e.target.value }))} placeholder="e.g. Oklahoma City, Norman" style={{ width: '100%' }} />
                    </div>
                    <div className="form-group">
                      <label>Token Price</label>
                      <input type="number" min="0" max="99" step="1" value={createForm.token_cost} onChange={e => setCreateForm(f => ({ ...f, token_cost: e.target.value }))} style={{ width: '120px' }} />
                    </div>
                    <div className="form-group">
                      <label>Headline Image</label>
                      <div style={{ fontSize: '0.78rem', color: '#e67e22', marginBottom: '0.35rem', fontWeight: 600 }}>Best results: 1200 × 675 px (16:9 landscape). Portrait or square images will be cropped on feed cards.</div>
                      {createForm.headline_image && (
                        <img src={createForm.headline_image} alt="Headline" style={{ display: 'block', maxWidth: '100%', maxHeight: '180px', objectFit: 'cover', borderRadius: '6px', marginBottom: '0.5rem', border: '1px solid #ddd' }} />
                      )}
                      <label style={{ display: 'inline-block', padding: '0.35rem 0.85rem', backgroundColor: '#f4f4f4', border: '1px solid #ccc', borderRadius: '5px', fontSize: '0.85rem', cursor: headlineUploading ? 'not-allowed' : 'pointer' }}>
                        {headlineUploading ? 'Uploading…' : createForm.headline_image ? 'Replace Image' : 'Upload Image'}
                        <input type="file" accept="image/*" style={{ display: 'none' }} onChange={handleHeadlineUpload} disabled={headlineUploading} />
                      </label>
                    </div>
                    <div className="form-group">
                      <label>{createMode === 'external' ? 'Teaser / Description' : 'Content'}{createMode === 'external' && <span style={{ fontWeight: 400, color: '#888', fontSize: '0.82rem', marginLeft: '0.4rem' }}>— shown on the article card before purchase</span>}</label>
                      {createMode === 'external' ? (
                        <textarea
                          value={createForm.content}
                          onChange={e => setCreateForm(f => ({ ...f, content: e.target.value }))}
                          required
                          placeholder="Write a short teaser that readers will see before purchasing access…"
                          style={{ width: '100%', minHeight: '120px', resize: 'vertical' }}
                        />
                      ) : (
                        <ArticleEditor initialContent={createForm.content} onChange={val => setCreateForm(f => ({ ...f, content: val }))} />
                      )}
                    </div>
                    {createError && (
                      <div style={{ padding: '0.65rem 0.9rem', margin: '0.75rem 0 0.25rem', borderRadius: '6px', backgroundColor: '#fee2e2', border: '1px solid #fca5a5', color: '#991b1b', fontSize: '0.875rem', lineHeight: 1.45 }}>
                        {createError}
                      </div>
                    )}
                    <div style={{ display: 'flex', gap: '0.6rem', flexWrap: 'wrap', marginTop: '1rem' }}>
                      <button type="submit" disabled={createSaving} style={{ padding: '0.55rem 1.25rem', backgroundColor: '#343a40', color: '#fff', border: 'none', borderRadius: '7px', fontWeight: '600', fontSize: '0.9rem', cursor: createSaving ? 'default' : 'pointer', opacity: createSaving ? 0.7 : 1 }}>
                        {createSaving ? 'Publishing…' : 'Publish Article'}
                      </button>
                      <button type="button" disabled={createSaving} onClick={(e) => handleCreateSubmit(e, true)} style={{ padding: '0.55rem 1.25rem', backgroundColor: '#f8d7da', color: '#721c24', border: '1px solid #f5c6cb', borderRadius: '7px', fontWeight: '600', fontSize: '0.9rem', cursor: createSaving ? 'default' : 'pointer' }}>
                        Save as Draft
                      </button>
                      <button
                        type="button"
                        onClick={() => setPreviewArticle({
                          title: createForm.title,
                          content: createForm.content,
                          genre_tree: createGenreTree,
                          writer_name: createForm.writer_name
                            ? createForm.writer_name.split(',').map(n => n.trim()).filter(n => n)
                            : [displayName],
                          headline_image: createForm.headline_image || null,
                          token_cost: createForm.token_cost,
                          date_created: new Date(),
                        })}
                        style={{ padding: '0.55rem 1.25rem', backgroundColor: '#f0f4ff', color: '#3366cc', border: '1px solid #c0cff8', borderRadius: '7px', fontWeight: '600', fontSize: '0.9rem', cursor: 'pointer' }}
                      >
                        Preview
                      </button>
                      <button type="button" onClick={() => { setShowCreateForm(false); setCreateError(''); }} style={{ padding: '0.55rem 1.25rem', backgroundColor: '#f4f4f4', color: '#333', border: '1px solid #ddd', borderRadius: '7px', fontSize: '0.9rem', cursor: 'pointer' }}>
                        Cancel
                      </button>
                    </div>
                  </form>
                </div>
              ) : (
                <button
                  onClick={() => setShowCreateForm(true)}
                  style={{ marginBottom: '1.25rem', padding: '0.55rem 1.25rem', backgroundColor: '#343a40', color: '#fff', border: 'none', borderRadius: '7px', fontWeight: '600', fontSize: '0.875rem', cursor: 'pointer' }}
                >
                  + Create New Article
                </button>
              )}

              {myArticles.length === 0 && !showCreateForm ? (
                <div style={{ textAlign: 'center', color: '#aaa', padding: '3rem' }}>No articles created by you yet.</div>
              ) : (
                [...myArticles]
                  .sort((a, b) => {
                    if (a.status === 'draft' && b.status !== 'draft') return -1;
                    if (b.status === 'draft' && a.status !== 'draft') return 1;
                    return new Date(b.date_created) - new Date(a.date_created);
                  })
                  .map(art => <MyArticleCard key={art._id} art={art} />)
              )}
            </>
          )}
        </div>

        {/* Right Sidebar */}
        <div className="sidebar sidebar-right">
          <div className="sidebar-title">Quick Actions</div>
          <div className="recommended-list">
            <div
              className="recommended-item"
              onClick={() => { setTab('my-articles'); setShowCreateForm(true); }}
              style={{ cursor: 'pointer' }}
            >
              <span className="recommended-icon">📝</span>
              <span className="recommended-text">Create New Article</span>
            </div>
          </div>
          <div className="sidebar-title" style={{ marginTop: '1.5rem' }}>Queue Summary</div>
          <div className="stats">
            {authors.map(a => {
              const pending = queue.filter(art => art.authorInfo?._id === a._id || art.writer_id === a._id).length;
              return (
                <div key={a._id} className="stat-item">
                  <div className="stat-value" style={{ color: pending > 0 ? '#e67e22' : '#1a1a1a', fontSize: '1.4rem' }}>{pending}</div>
                  <div className="stat-label">{a.display_name}</div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Article preview modal */}
      {previewArticle && (
        <ArticlePreview article={previewArticle} onClose={() => setPreviewArticle(null)} />
      )}

      {/* Reject modal */}
      {rejectTarget && (
        <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ backgroundColor: '#fff', borderRadius: '12px', padding: '2rem', maxWidth: '440px', width: '90%', boxShadow: '0 8px 32px rgba(0,0,0,0.18)' }}>
            <h4 style={{ marginBottom: '0.5rem' }}>Reject Article</h4>
            <p style={{ fontSize: '0.88rem', color: '#555', marginBottom: '1rem' }}>
              <strong>"{rejectTarget.title}"</strong> will be returned to the author as rejected.
            </p>
            <label style={{ fontSize: '0.82rem', fontWeight: '600', display: 'block', marginBottom: '0.35rem' }}>
              Feedback for the author (optional)
            </label>
            <textarea
              value={rejectNote}
              onChange={e => setRejectNote(e.target.value)}
              placeholder="Explain what needs to be revised..."
              rows={4}
              autoFocus
              style={{ width: '100%', padding: '0.55rem 0.75rem', fontSize: '0.875rem', border: '1px solid #ccc', borderRadius: '6px', marginBottom: '1rem', boxSizing: 'border-box', resize: 'vertical' }}
            />
            <div style={{ display: 'flex', gap: '0.6rem' }}>
              <button
                onClick={handleRejectSubmit}
                style={{ flex: 1, padding: '0.65rem', backgroundColor: '#dc3545', color: '#fff', border: 'none', borderRadius: '7px', fontWeight: '600', fontSize: '0.9rem', cursor: 'pointer' }}
              >
                Confirm Reject
              </button>
              <button
                onClick={() => { setRejectTarget(null); setRejectNote(''); }}
                style={{ flex: 1, padding: '0.65rem', backgroundColor: '#f4f4f4', color: '#333', border: '1px solid #ddd', borderRadius: '7px', fontSize: '0.9rem', cursor: 'pointer' }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Editor direct-edit modal (for author submissions) */}
      {editTarget && (
        <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.55)', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', zIndex: 1000, overflowY: 'auto', padding: '2rem 1rem' }}>
          <div style={{ backgroundColor: '#fff', borderRadius: '12px', padding: '2rem', maxWidth: '760px', width: '100%', boxShadow: '0 8px 32px rgba(0,0,0,0.18)' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1.25rem' }}>
              <h4 style={{ margin: 0 }}>✏️ Edit Article</h4>
              <button onClick={() => setEditTarget(null)} aria-label="Close" style={{ background: 'none', border: 'none', fontSize: '1.3rem', cursor: 'pointer', color: '#aaa' }}>×</button>
            </div>
            <div style={{ marginBottom: '1rem' }}>
              <label style={{ fontSize: '0.82rem', fontWeight: '600', display: 'block', marginBottom: '0.3rem' }}>Title</label>
              <input type="text" value={editForm.title} onChange={e => setEditForm(f => ({ ...f, title: e.target.value }))} style={{ width: '100%', padding: '0.55rem 0.75rem', fontSize: '0.9rem', border: '1px solid #ccc', borderRadius: '6px', boxSizing: 'border-box' }} />
            </div>
            <div style={{ marginBottom: '1rem' }}>
              <label style={{ fontSize: '0.82rem', fontWeight: '600', display: 'block', marginBottom: '0.3rem' }}>Content</label>
              <ArticleEditor initialContent={editForm.content} onChange={val => setEditForm(f => ({ ...f, content: val }))} />
            </div>
            {editTarget.purchased > 0 && (
              <div style={{ marginBottom: '1rem' }}>
                <label style={{ fontSize: '0.82rem', fontWeight: '600', display: 'block', marginBottom: '0.3rem' }}>
                  Edit Note <span style={{ fontWeight: 400, color: '#888' }}>(optional — shown to readers who purchased this article)</span>
                </label>
                <textarea value={editForm.editNote} onChange={e => setEditForm(f => ({ ...f, editNote: e.target.value }))} placeholder="Briefly describe what was changed..." rows={2} style={{ width: '100%', padding: '0.55rem 0.75rem', fontSize: '0.875rem', border: '1px solid #ccc', borderRadius: '6px', boxSizing: 'border-box', resize: 'vertical' }} />
              </div>
            )}
            {actionMsg && actionMsg.includes('alt text') && (
              <div style={{ padding: '0.65rem 0.9rem', marginBottom: '0.75rem', borderRadius: '6px', backgroundColor: '#fee2e2', border: '1px solid #fca5a5', color: '#991b1b', fontSize: '0.875rem', lineHeight: 1.45 }}>
                {actionMsg}
              </div>
            )}
            <div style={{ display: 'flex', gap: '0.6rem' }}>
              <button onClick={handleEditorEditSave} disabled={editSaving} style={{ flex: 2, padding: '0.65rem', backgroundColor: '#343a40', color: '#fff', border: 'none', borderRadius: '7px', fontWeight: '600', fontSize: '0.9rem', cursor: editSaving ? 'default' : 'pointer', opacity: editSaving ? 0.7 : 1 }}>
                {editSaving ? 'Saving…' : 'Save Changes'}
              </button>
              <button onClick={() => setEditTarget(null)} style={{ flex: 1, padding: '0.65rem', backgroundColor: '#f4f4f4', color: '#333', border: '1px solid #ddd', borderRadius: '7px', fontSize: '0.9rem', cursor: 'pointer' }}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit own article modal */}
      {editOwnTarget && (
        <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.55)', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', zIndex: 1000, overflowY: 'auto', padding: '2rem 1rem' }}>
          <div style={{ backgroundColor: '#fff', borderRadius: '12px', padding: '2rem', maxWidth: '760px', width: '100%', boxShadow: '0 8px 32px rgba(0,0,0,0.18)' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1.25rem' }}>
              <h4 style={{ margin: 0 }}>✏️ Edit My Article</h4>
              <button onClick={() => setEditOwnTarget(null)} aria-label="Close" style={{ background: 'none', border: 'none', fontSize: '1.3rem', cursor: 'pointer', color: '#aaa' }}>×</button>
            </div>
            <div style={{ marginBottom: '1rem' }}>
              <label style={{ fontSize: '0.82rem', fontWeight: '600', display: 'block', marginBottom: '0.3rem' }}>Title</label>
              <input type="text" value={editOwnForm.title} onChange={e => setEditOwnForm(f => ({ ...f, title: e.target.value }))} style={{ width: '100%', padding: '0.55rem 0.75rem', fontSize: '0.9rem', border: '1px solid #ccc', borderRadius: '6px', boxSizing: 'border-box' }} />
            </div>
            <div style={{ marginBottom: '1rem' }}>
              <label style={{ fontSize: '0.82rem', fontWeight: '600', display: 'block', marginBottom: '0.3rem' }}>Content</label>
              <ArticleEditor initialContent={editOwnForm.content} onChange={val => setEditOwnForm(f => ({ ...f, content: val }))} />
            </div>
            {editOwnTarget.purchased > 0 && (
              <div style={{ marginBottom: '1rem' }}>
                <label style={{ fontSize: '0.82rem', fontWeight: '600', display: 'block', marginBottom: '0.3rem' }}>
                  Edit Note <span style={{ fontWeight: 400, color: '#888' }}>(optional — shown to readers)</span>
                </label>
                <textarea value={editOwnForm.editNote} onChange={e => setEditOwnForm(f => ({ ...f, editNote: e.target.value }))} placeholder="Briefly describe what was changed..." rows={2} style={{ width: '100%', padding: '0.55rem 0.75rem', fontSize: '0.875rem', border: '1px solid #ccc', borderRadius: '6px', boxSizing: 'border-box', resize: 'vertical' }} />
              </div>
            )}
            {actionMsg && actionMsg.includes('alt text') && (
              <div style={{ padding: '0.65rem 0.9rem', marginBottom: '0.75rem', borderRadius: '6px', backgroundColor: '#fee2e2', border: '1px solid #fca5a5', color: '#991b1b', fontSize: '0.875rem', lineHeight: 1.45 }}>
                {actionMsg}
              </div>
            )}
            <div style={{ display: 'flex', gap: '0.6rem' }}>
              <button onClick={handleEditOwnSave} disabled={editOwnSaving} style={{ flex: 2, padding: '0.65rem', backgroundColor: '#343a40', color: '#fff', border: 'none', borderRadius: '7px', fontWeight: '600', fontSize: '0.9rem', cursor: editOwnSaving ? 'default' : 'pointer', opacity: editOwnSaving ? 0.7 : 1 }}>
                {editOwnSaving ? 'Saving…' : 'Save Changes'}
              </button>
              <button onClick={() => setEditOwnTarget(null)} style={{ flex: 1, padding: '0.65rem', backgroundColor: '#f4f4f4', color: '#333', border: '1px solid #ddd', borderRadius: '7px', fontSize: '0.9rem', cursor: 'pointer' }}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default EditorDashboard;
